import SyntaxHighlighter from 'react-syntax-highlighter';
import { Button } from 'components/ui_library/Button';
import { textLegal, textLinkExternal } from 'modules/Auth/components/styles';
import { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'components/Tabs';
import { Select } from 'components/Select';
import { either } from 'fp-ts';
import { reactLeftToJSX } from 'utils/uiStates/uiStates';
import { pipe } from 'utils/fp';
import { observer } from 'mobx-react-lite';
import highlightJsTomorrowNightStyles from 'react-syntax-highlighter/dist/cjs/styles/hljs/tomorrow-night';
import { useMstInstanceCreator } from 'utils/mst/useMstInstanceCreator';
import { createAppTokensStore } from 'modules/Apps/mstStores/UserKeysMST';
import { copyToClipboard } from 'utils/copyToClipboard';
import { formatTimestamp } from 'components/Table';
import { both } from 'rambda';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';
import { isModel } from 'utils/typeguards/cfEntities';
import { parseISO, isFuture } from 'date-fns';
import { useAuthState } from 'modules/Auth/AuthContext';
import { flex } from 'styles/utils';
import { getModelSignaturesFromModelVersion } from 'modules/Playground/hooks/utils/useFetchModelOutput/getModelSignaturesFromModelVersion';
import { ApiModel, ApiModelVersion } from 'types/swagger/clarifai-api';
import { computeDocumentation } from 'modules/Compute/utils';
import { getCodeSnippet, ModelLanguage, WorkflowSupportedLanguages } from '../CodeSnippets';
import {
  codeWrapper,
  activeTabClass,
  tabApiWrapper,
  tabApiDropdownWrapper,
  dropdownOptionStyles,
  selectElementOverrideStyles,
} from './TabAPI.styles';

type TabsList = Array<{ id: ModelLanguage; title: ModelLanguage }>;

export type TabApiProps =
  | {
      entity: CF.API.Models.Model;
      handleCreateNewKey: () => void;
      closeAction: () => void;
    }
  | {
      entity: CF.API.Workflows.Workflow;
      workflowInputType?: string;
      handleCreateNewKey: () => void;
      closeAction: () => void;
    };

type TabApiInternalProps = TabApiProps & {
  tabsList: TabsList;
  accessTokensList: CF.API.ApiKeys.KeyData[];
  closeAction: () => void;
};

const TABID_TO_LANGUAGE = {
  ...ModelLanguage,
  [ModelLanguage.PythonSDK]: ModelLanguage.Python,
  [ModelLanguage.PythonOpenAI]: ModelLanguage.Python,
  [ModelLanguage.PythonMCP]: ModelLanguage.Python,
  [ModelLanguage.NodeJS]: ModelLanguage.Javascript,
  [ModelLanguage.NodeJS_SDK]: ModelLanguage.Javascript,
  [ModelLanguage.NodeJSOpenAI]: ModelLanguage.Javascript,
  [ModelLanguage.NodeJSVercel]: ModelLanguage.Javascript,
  [ModelLanguage.cURL]: 'bash',
};

export function checkIfPATHasPredictAccess(isModelEntity: boolean) {
  return (PAT: CF.API.ApiKeys.KeyData) => {
    const notExpired = (token: CF.API.ApiKeys.KeyData) => (token.expires_at ? isFuture(parseISO(token.expires_at)) : true);
    const minPermissions = isModelEntity ? permissionMatchers.model.predict : permissionMatchers.workflow.predict;
    const validToken = both(notExpired, minPermissions);
    return validToken(PAT);
  };
}

const accessTokenToTitle = (accessToken?: CF.API.ApiKeys.KeyData) => `${accessToken?.description} (${formatTimestamp(accessToken?.created_at)})`;

const DropdownOption: React.FC<{ accessToken: CF.API.ApiKeys.KeyData }> = ({ accessToken }) => (
  <div className={dropdownOptionStyles}>
    <span className="token">{accessToken.description}</span>
    <span className="timestamp">({formatTimestamp(accessToken.created_at)})</span>
  </div>
);

const TabAPIInternal: React.FC<TabApiInternalProps> = observer(
  ({ entity, tabsList, accessTokensList, handleCreateNewKey, closeAction, ...props }) => {
    const [activeTabId, setActiveTabId] = useState<ModelLanguage>(tabsList[0].id);
    // Access Token's ID which is selected from dropdown
    const [selectedAccessTokenId, setSelectedAccessTokenId] = useState<string | undefined>('INSERT_YOUR_PAT_HERE');
    const selectedAccessToken = useMemo(() => accessTokensList.find((token) => token.id === selectedAccessTokenId), [selectedAccessTokenId]);

    const dropdownOptions = useMemo(
      () =>
        accessTokensList.map((accessToken) => ({
          name: accessTokenToTitle(accessToken),
          value: accessToken.id,
          content: <DropdownOption accessToken={accessToken} />,
        })),
      [accessTokensList],
    );

    const onCopyBtnClikced = () => {
      copyToClipboard(codeSnippet, 'Code copied to clipboard!')();
      closeAction?.();
    };

    let codeSnippet = '';

    if (isModel(entity)) {
      const method_signatures = getModelSignaturesFromModelVersion(entity.model_version as unknown as ApiModelVersion, entity as unknown as ApiModel);
      codeSnippet = getCodeSnippet({ language: activeTabId, entity, personalAccessToken: selectedAccessTokenId, method_signatures });
    } else {
      codeSnippet = getCodeSnippet({
        language: activeTabId,
        entity,
        personalAccessToken: selectedAccessTokenId,
        ...props,
      });
    }

    // By default, first valid access token will be used in both code snippet & dropdown
    // https://clarifai.atlassian.net/browse/MRK-2022
    useEffect(() => {
      if (accessTokensList.length > 0) {
        const oldestToken = accessTokensList.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];
        setSelectedAccessTokenId(oldestToken?.id);
      }
    }, [accessTokensList]);

    return (
      <div className={tabApiWrapper}>
        <div className={flex.flex.one}>
          <Tabs
            aria-label="Language"
            className="tabs"
            activeTabClassName={activeTabClass}
            tabs={tabsList}
            activeTab={activeTabId}
            setActiveTab={(tabId) => setActiveTabId(tabId as ModelLanguage)}
          />
        </div>

        <div className={tabApiDropdownWrapper}>
          <div className={flex.flex.one} style={{ minWidth: 0 }}>
            <Select
              modal
              data-testid="tokens-dropdown"
              options={dropdownOptions}
              title={
                accessTokensList.length ? (
                  <DropdownOption accessToken={selectedAccessToken || accessTokensList[0]} />
                ) : (
                  'No PATs with predict scope exist. Please create one'
                )
              }
              onChange={({ value }) => setSelectedAccessTokenId(value)}
              selectElementClassName={selectElementOverrideStyles}
            />
          </div>

          <Button size="md" variant="tertiary" onClick={handleCreateNewKey}>
            Create a new token
          </Button>
        </div>

        <div className={codeWrapper}>
          <Button variant="tertiary" onClick={onCopyBtnClikced}>
            Copy Code
          </Button>

          <div className="preview">
            <SyntaxHighlighter
              language={TABID_TO_LANGUAGE[activeTabId].toLowerCase()}
              showLineNumbers
              lineNumberStyle={{
                borderRight: '1px solid #c5c8c6',
                marginRight: '0.5rem',
              }}
              style={highlightJsTomorrowNightStyles}
            >
              {codeSnippet}
            </SyntaxHighlighter>
          </div>
        </div>

        <div className={textLegal}>
          Need help? Check our&nbsp;
          <a className={textLinkExternal} href={computeDocumentation.quickstart} target="_blank" rel="noreferrer">
            complete quickstart
          </a>
          &nbsp;documentation
        </div>
      </div>
    );
  },
);

export const TabAPI: React.FC<TabApiProps> = observer((props): JSX.Element => {
  const { authData } = useAuthState();
  // Always fetch the PATs for logged-in user, as PATs for org returns error:
  // https://clarifai.atlassian.net/browse/MRK-2815
  const userId = authData?.user_id;
  const store = useMstInstanceCreator(createAppTokensStore, []);
  const isModelEntity = isModel(props.entity);
  const filterValidPAT = checkIfPATHasPredictAccess(isModelEntity);
  const tabsList = Object.values(isModelEntity ? ModelLanguage : WorkflowSupportedLanguages).map((title) => ({ id: title, title })) as TabsList;

  useEffect(() => {
    if (userId) {
      store.load({ userId });
    }
  }, [userId]);

  return pipe(
    store.listAccessTokens,
    // This filters out the access tokens which are expired and/or without minimum permissions for model/workflow's prediction
    either.chain((accessTokensList) => {
      return either.right(accessTokensList.filter(filterValidPAT) as unknown as CF.API.ApiKeys.KeyData[]);
    }),
    either.fold(
      reactLeftToJSX({
        EMPTY: () => <TabAPIInternal accessTokensList={[]} tabsList={tabsList} {...props} />,
      }),
      (accessTokensList) => <TabAPIInternal accessTokensList={accessTokensList} tabsList={tabsList} {...props} />,
    ),
  );
});
